import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ACCENT, Field, PrimaryButton, SecondaryButton, inputClass, ringStyle } from "./shared";
import TerritoryPicker from "./TerritoryPicker";
import {
  MESSAGES,
  checkReplacementAudio,
  fieldErrors,
  formatBytes,
  loadGenres,
  normaliseFile,
  sha256Hex,
  trackApi,
  trackErrorMessage,
  uploadToStorage,
} from "./trackApi";

// Dialogs for Track Administration, shared by the catalogue and the track page:
// - EditTrackDialog: title, genre and (optionally) a replacement audio file for a track that is submitted or live.
// - TakeDownDialog: the confirmation shown before a track is taken down.

// Title and genre are mandatory; a title of only spaces counts as empty.
export function validateLiveTitle(value) {
  const t = String(value ?? "").trim();
  if (!t) return "Please enter a track title.";
  if (t.length > 200) return "Track title must be at most 200 characters.";
  if (/[<>]/.test(t)) return "Track title cannot contain < or >.";
  return "";
}

// The server's own sentence is used for problems with the file (wrong type, too long, quality too low, duplicate).
const saveErrorMessage = (e) => {
  const status = e?.response?.status;
  const message = e?.response?.data?.message;
  if (message && [400, 409, 413, 415, 422].includes(status)) return message;
  return trackErrorMessage(e, MESSAGES[500]);
};

export function EditTrackDialog({ track, onCancel, onSaved }) {
  const [title, setTitle] = useState(track.title || "");
  const [genreId, setGenreId] = useState(track.genre ? String(track.genre.id) : "");
  const [territories, setTerritories] = useState(Array.isArray(track.territories) ? track.territories : []);
  const [genres, setGenres] = useState([]);
  const [rules, setRules] = useState(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState("idle"); // idle | uploading | saving
  const [progress, setProgress] = useState(0);
  const controller = useRef(null);
  const inputRef = useRef(null);
  const mounted = useRef(true);
  const busy = phase !== "idle";

  useEffect(() => {
    mounted.current = true;
    loadGenres().then((rows) => mounted.current && setGenres(rows)).catch(() => {});
    trackApi.rules().then((r) => mounted.current && setRules(r)).catch(() => {});
    return () => {
      mounted.current = false;
      if (controller.current) controller.current.abort();
    };
  }, []);

  const options = track.genre && !genres.some((g) => g.id === track.genre.id) ? [...genres, track.genre] : genres;

  const choose = (raw) => {
    if (!raw || busy) return;
    const picked = normaliseFile(raw);
    const problem = checkReplacementAudio(picked, rules);
    if (problem) {
      setFile(null);
      setErrors((prev) => ({ ...prev, file: problem }));
      return;
    }
    setErrors((prev) => ({ ...prev, file: undefined }));
    setFile(picked);
  };

  // Cancel closes without saving anything; an upload in progress is stopped.
  const cancel = () => {
    if (controller.current) controller.current.abort();
    onCancel();
  };

  const save = async () => {
    if (busy) return;
    const found = {};
    const titleError = validateLiveTitle(title);
    if (titleError) found.title = titleError;
    if (!genreId) found.genre_id = "Please choose a genre.";
    if (file) {
      const problem = checkReplacementAudio(file, rules);
      if (problem) found.file = problem;
    }
    setErrors(found);
    if (Object.keys(found).length) return;

    const body = { track_id: track.id };
    if (title.trim() !== (track.title || "")) body.title = title.trim();
    if (String(track.genre?.id || "") !== genreId) body.genre_id = Number(genreId);
    const before = JSON.stringify([...(Array.isArray(track.territories) ? track.territories : [])].sort());
    if (JSON.stringify([...territories].sort()) !== before) body.territories = territories;
    if (!file && Object.keys(body).length === 1) {
      onCancel(); // nothing changed
      return;
    }

    try {
      if (file) {
        const ctrl = new AbortController();
        controller.current = ctrl;
        setPhase("uploading");
        setProgress(0);
        const hashing = sha256Hex(file);
        const stored = await uploadToStorage(file, { signal: ctrl.signal, onProgress: (p) => mounted.current && setProgress(p) });
        body.audio = { ...stored, file_hash: await hashing };
      }
      if (!mounted.current) return;
      setPhase("saving");
      const updated = await trackApi.updateLive(body);
      toast.success(file ? "Changes saved. Your new audio file is waiting for review." : "Track details saved.");
      onSaved(updated);
    } catch (e) {
      if (!mounted.current || e?.code === "ERR_CANCELED") return;
      const fields = fieldErrors(e);
      const message = e?.message && !e.response ? e.message : saveErrorMessage(e);
      if (fields) setErrors(fields);
      else if (file) setErrors({ file: message });
      toast.error(`${message} Your track has not been changed.`);
      setPhase("idle");
    }
  };

  const fileHint = `WAV, MP3 or FLAC${rules?.audio?.max_bytes ? `, up to ${formatBytes(rules.audio.max_bytes)}` : ""}`;

  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center bg-black/40 px-4 py-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="edit-track-title" data-testid="edit-live-dialog">
      <div className="w-full max-w-lg rounded-2xl bg-white p-7 shadow-xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4">
          <h2 id="edit-track-title" className="text-2xl font-bold text-gray-900">Edit track</h2>
          <button type="button" onClick={cancel} aria-label="Close" className="text-gray-500 text-2xl leading-none">×</button>
        </div>
        <p className="text-gray-600 mt-2 text-sm">
          You can change the title, the genre, where the track can be heard and the audio file. A new audio file is checked first and, while the track is in review or has licences, goes through review before
          listeners or licensees get it. Until then the current audio stays available.
        </p>

        <div className="space-y-4 mt-5">
          <Field label="Title" required error={errors.title}>
            <input className={inputClass(errors.title)} style={ringStyle} aria-label="Track title" value={title} maxLength={200} disabled={busy} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Genre" required error={errors.genre_id}>
            <select className={inputClass(errors.genre_id)} style={ringStyle} aria-label="Genre" value={genreId} disabled={busy} onChange={(e) => setGenreId(e.target.value)}>
              <option value="">Choose a genre</option>
              {options.map((g) => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
            </select>
          </Field>

          <TerritoryPicker value={territories} onChange={setTerritories} disabled={busy} />

          <Field label="Replace audio file" hint={`Optional. ${fileHint}.`} error={errors.file}>
            <div
              className={`rounded-xl border-2 border-dashed px-4 py-5 text-center text-sm ${dragging ? "bg-[#f1f6f2]" : "bg-white"} ${errors.file ? "border-red-400" : "border-gray-300"}`}
              style={dragging ? { borderColor: ACCENT } : undefined}
              onDragOver={(e) => {
                e.preventDefault();
                if (!busy) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                choose(e.dataTransfer?.files?.[0]);
              }}
              data-testid="replace-dropzone"
            >
              {file ? (
                <div className="flex items-center justify-between gap-3 text-left">
                  <span className="min-w-0">
                    <span className="block font-semibold text-gray-900 truncate">{file.name}</span>
                    <span className="text-gray-500">{formatBytes(file.size)}</span>
                  </span>
                  {!busy && <button type="button" className="text-sm font-semibold underline shrink-0" onClick={() => setFile(null)}>Remove</button>}
                </div>
              ) : (
                <>
                  <p className="text-gray-700">Drag and drop a file here, or</p>
                  <button type="button" className="mt-1 font-semibold underline" style={{ color: ACCENT }} disabled={busy} onClick={() => inputRef.current && inputRef.current.click()}>
                    Choose a file
                  </button>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept=".wav,.mp3,.flac,audio/wav,audio/mpeg,audio/flac"
                aria-label="Replacement audio file"
                data-testid="replace-input"
                onChange={(e) => {
                  choose(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </div>
            {phase === "uploading" && (
              <div className="mt-2" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                <div className="h-2 rounded-full bg-gray-200 overflow-hidden"><div className="h-full" style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: ACCENT }} /></div>
                <p className="text-xs text-gray-500 mt-1">Uploading… {Math.round(progress * 100)}%</p>
              </div>
            )}
            {phase === "saving" && <p className="text-xs text-gray-500 mt-2">Checking your audio file…</p>}
          </Field>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <SecondaryButton onClick={cancel} data-testid="edit-live-cancel">Cancel</SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy} data-testid="edit-live-save">
            {phase === "uploading" ? "Uploading…" : phase === "saving" ? "Saving…" : "Save Changes"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// FSD 6.2.4: with licences already sold on the track, the artist types the track title before Take Down Track is enabled.
export function TakeDownDialog({ track, busy, onCancel, onConfirm }) {
  const live = track.display_status === "published";
  const name = track.title || "this track";
  const sold = live ? Number(track.metrics?.licenses || 0) || 0 : 0;
  const [typed, setTyped] = useState("");
  const mustType = sold > 0 && !!track.title;
  const confirmed = !mustType || typed.trim() === String(track.title).trim();
  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center bg-black/40 px-4 py-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="take-down-title" data-testid="take-down-dialog">
      <div className="w-full max-w-lg rounded-2xl bg-white p-7 shadow-xl my-8">
        <div className="flex items-start justify-between gap-4">
          <h2 id="take-down-title" className="text-2xl font-bold text-gray-900">
            {live ? `Take down “${name}”?` : `Withdraw “${name}” from review?`}
          </h2>
          <button type="button" onClick={onCancel} aria-label="Close" className="text-gray-500 text-2xl leading-none">×</button>
        </div>
        {live ? (
          <div className="mt-3 space-y-3 text-sm" data-testid="take-down-note">
            <p className="rounded-xl bg-amber-50 text-amber-900 px-4 py-3">
              Removing the track stops it from appearing in discovery, stops fan-page plays and prevents new licences from being purchased.
            </p>
            <p className="rounded-xl bg-gray-50 text-gray-700 px-4 py-3">
              {sold > 0 ? `${sold} ${sold === 1 ? "licence" : "licences"} already sold on this track stay valid. ` : "Licences already sold remain valid. "}
              Buyers who published content while the licence was in force can keep that content live, subject to the applicable licence terms.
              {sold > 0 && " Automatic renewal is switched off and each licence holder is told."}
            </p>
            <p className="rounded-xl bg-red-50 text-red-800 px-4 py-3">
              The takedown cannot be undone from this screen. You must re-upload the track to make it available again.
            </p>
            {mustType && (
              <label className="block text-gray-700">
                Type <span className="font-semibold">{track.title}</span> to confirm
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  disabled={busy}
                  className={`${inputClass(false)} mt-1`}
                  style={ringStyle}
                  autoComplete="off"
                  data-testid="take-down-type-title"
                />
              </label>
            )}
          </div>
        ) : (
          <p className="text-gray-600 mt-3 text-sm" data-testid="take-down-note">
            This pulls the track out of review and turns it back into a draft. You can edit it and submit it again.
          </p>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <SecondaryButton onClick={onCancel} disabled={busy} data-testid="take-down-cancel">Cancel</SecondaryButton>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || !confirmed}
            className="rounded-full font-semibold text-white px-7 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-60"
            data-testid="take-down-confirm"
          >
            {busy ? "Working…" : live ? "Take Down Track" : "Withdraw Track"}
          </button>
        </div>
      </div>
    </div>
  );
}
